import { Button } from "@/components/ui/button"
import { Card, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useParams, useLocation } from "wouter"
import { ArrowRight, ArrowLeft, Target, Plus, X } from "lucide-react"
import { useValueAssessment, SaveSummarySectionOptions } from "@/contexts/ValueAssessmentContext"
import { useState, useEffect, useCallback, useRef } from "react"
import { STANDARD_PILLARS, PILLAR_PAIN_POINTS, migrateToCanonicalPillars } from "@shared/pillar-constants"
import { useUnsavedChangesWarning } from "@/hooks/useUnsavedChangesWarning"

// Use standardized pillar definitions - canonical names only
const availablePillars = STANDARD_PILLARS
const painPointOptions = PILLAR_PAIN_POINTS

export default function PlatformConfigurationScreen() {
  const params = useParams()
  const [, setLocation] = useLocation()
  
  // Get projectId from URL params (traditional approach)
  const projectId = params.projectId
  const { 
    data, 
    updateSection,
    saveSummarySection
  } = useValueAssessment()

  // State using canonical pillar names
  const [selectedPillars, setSelectedPillars] = useState<string[]>([])
  const [pillarPainPoints, setPillarPainPoints] = useState<Record<string, string[]>>({})
  const [customPainPoints, setCustomPainPoints] = useState<Record<string, string[]>>({})
  const [newCustomPainPoint, setNewCustomPainPoint] = useState<Record<string, string>>({})
  const [hasLocalChanges, setHasLocalChanges] = useState(false)
  const hasHydratedRef = useRef(false)

  // Generate pillar priorities based on selected pillars
  const generatePillarPriorities = (pillars: string[]): Record<string, number> => {
    const priorities: Record<string, number> = {}
    pillars.forEach((pillar, index) => {
      priorities[pillar] = index + 1 // Priority 1, 2, 3, etc.
    })
    return priorities
  }

  const buildPillarSelectionData = useCallback(() => {
    const mergedPainPoints: Record<string, string[]> = {}
    selectedPillars.forEach(pillar => {
      const standardPoints = pillarPainPoints[pillar] || []
      const customPoints = customPainPoints[pillar] || []
      mergedPainPoints[pillar] = [...standardPoints, ...customPoints]
    })

    return {
      selectedPillars: selectedPillars,
      pillarPainPoints: mergedPainPoints,
      customPainPoints: customPainPoints,
      pillarPriorities: generatePillarPriorities(selectedPillars)
    }
  }, [selectedPillars, pillarPainPoints, customPainPoints])

  const persistPillarSelection = useCallback(async (options?: SaveSummarySectionOptions) => {
    const pillarSelectionData = buildPillarSelectionData()

    await saveSummarySection({
      pillarSelection: pillarSelectionData,
      valueSummary: {
        pillarSelection: pillarSelectionData
      },
      lastUpdatedAt: new Date().toISOString(),
      schemaVersion: 'v2.1'
    }, options)

    updateSection('pillarSelection', pillarSelectionData, { autoSave: false })
    setHasLocalChanges(false)
  }, [buildPillarSelectionData, saveSummarySection, updateSection])

  const persistPillarSelectionWithCurrent = useCallback((options?: SaveSummarySectionOptions) => {
    return persistPillarSelection(options)
  }, [persistPillarSelection])

  // Handle pillar selection with canonical names
  const handlePillarToggle = (pillarName: string, checked: boolean) => {
    console.log(`🔄 [INPUT CHANGE] Platform Configuration pillar ${pillarName} toggled:`, checked);
    
    let updatedPillars: string[]
    
    if (checked) {
      updatedPillars = [...selectedPillars, pillarName]
    } else {
      updatedPillars = selectedPillars.filter(p => p !== pillarName)
      // Remove pain points for deselected pillar
      setPillarPainPoints(prev => {
        const updated = { ...prev }
        delete updated[pillarName]
        return updated
      })
    }
    
    setSelectedPillars(updatedPillars)
    setHasLocalChanges(true)
    
    // Create pillar selection data for immediate update
    const pillarSelectionData = {
      selectedPillars: updatedPillars,
      pillarPainPoints: pillarPainPoints,
      pillarPriorities: generatePillarPriorities(updatedPillars)
    }
    
    // Update pillarSelection section with pillar selection data
    updateSection('pillarSelection', pillarSelectionData, { autoSave: false })
  }

  // Handle pain point selection
  const handlePainPointToggle = (pillarName: string, painPoint: string, checked: boolean) => {
    console.log(`🔄 [INPUT CHANGE] Platform Configuration pain point ${pillarName}:${painPoint} toggled:`, checked);
    
    const updatedPainPoints = { ...pillarPainPoints }
    const pillarPainPointsArray = updatedPainPoints[pillarName] || []
    
    if (checked) {
      updatedPainPoints[pillarName] = [...pillarPainPointsArray, painPoint]
    } else {
      updatedPainPoints[pillarName] = pillarPainPointsArray.filter(pp => pp !== painPoint)
    }
    
    setPillarPainPoints(updatedPainPoints)
    setHasLocalChanges(true)
    
    // Immediately update pillarSelection section to trigger auto-save
    const pillarSelectionData = {
      selectedPillars: selectedPillars,
      pillarPainPoints: updatedPainPoints,
      pillarPriorities: generatePillarPriorities(selectedPillars)
    }
    
    console.log(`💾 [PAIN POINT CHANGE] Updating pillarSelection with pain points:`, pillarSelectionData);
    updateSection('pillarSelection', pillarSelectionData, { autoSave: false })
  }

  // Add custom pain point
  const handleAddCustomPainPoint = (pillarName: string) => {
    const newPainPoint = newCustomPainPoint[pillarName]?.trim()
    if (!newPainPoint) return

    const updatedCustomPainPoints = { ...customPainPoints }
    const existingCustomPoints = updatedCustomPainPoints[pillarName] || []
    
    if (!existingCustomPoints.includes(newPainPoint)) {
      updatedCustomPainPoints[pillarName] = [...existingCustomPoints, newPainPoint]
      setCustomPainPoints(updatedCustomPainPoints)
      setHasLocalChanges(true)
      
      // Clear the input
      setNewCustomPainPoint(prev => ({ ...prev, [pillarName]: '' }))
      
      console.log(`✅ Added custom pain point to ${pillarName}:`, newPainPoint)
      
      // Merge with standard pain points and update pillarSelection section
      const mergedPainPoints: Record<string, string[]> = { ...pillarPainPoints }
      selectedPillars.forEach(pillar => {
        const standardPoints = pillarPainPoints[pillar] || []
        const customPoints = pillar === pillarName ? updatedCustomPainPoints[pillar] : (customPainPoints[pillar] || [])
        mergedPainPoints[pillar] = [...standardPoints, ...customPoints]
      })
      
      const pillarSelectionData = {
        selectedPillars: selectedPillars,
        pillarPainPoints: mergedPainPoints,
        customPainPoints: updatedCustomPainPoints,
        pillarPriorities: generatePillarPriorities(selectedPillars)
      }
      
      console.log(`💾 [CUSTOM PAIN POINT] Updating pillarSelection with merged pain points:`, pillarSelectionData);
      updateSection('pillarSelection', pillarSelectionData, { autoSave: false })
    }
  }

  // Remove custom pain point
  const handleRemoveCustomPainPoint = (pillarName: string, painPoint: string) => {
    const updatedCustomPainPoints = { ...customPainPoints }
    const existingCustomPoints = updatedCustomPainPoints[pillarName] || []
    
    updatedCustomPainPoints[pillarName] = existingCustomPoints.filter(pp => pp !== painPoint)
    setCustomPainPoints(updatedCustomPainPoints)
    setHasLocalChanges(true)
    
    console.log(`🗑️ Removed custom pain point from ${pillarName}:`, painPoint)
    
    // Merge with standard pain points and update pillarSelection section
    const mergedPainPoints: Record<string, string[]> = { ...pillarPainPoints }
    selectedPillars.forEach(pillar => {
      const standardPoints = pillarPainPoints[pillar] || []
      const customPoints = pillar === pillarName ? updatedCustomPainPoints[pillar] : (customPainPoints[pillar] || [])
      mergedPainPoints[pillar] = [...standardPoints, ...customPoints]
    })
    
    const pillarSelectionData = {
      selectedPillars: selectedPillars,
      pillarPainPoints: mergedPainPoints,
      customPainPoints: updatedCustomPainPoints,
      pillarPriorities: generatePillarPriorities(selectedPillars)
    }
    
    console.log(`💾 [REMOVE CUSTOM PAIN POINT] Updating pillarSelection with merged pain points:`, pillarSelectionData);
    updateSection('pillarSelection', pillarSelectionData, { autoSave: false })
  }

  // Hydration from stored data with canonical format
  useEffect(() => {
    // Use pillarSelection as single source of truth with proper typing
    // const pillarData = data.pillarSelection as any || {}
    // const pillarData = data?.summary_data?.pillarSelection ?? {}
    const pillarData = data?.summary_data?.pillarSelection
    
    console.log('[HYDRATE] Platform Configuration - clean data source:', {
      hasPillarSelection: !!data.pillarSelection,
      pillarSelectionKeys: data.pillarSelection ? Object.keys(data.pillarSelection) : [],
      dataSource: 'pillarSelection'
    })
    
    console.log('[HYDRATE] Platform Configuration - pillar data:', pillarData)
    console.log('[HYDRATE] Platform Configuration - pain points data:', {
      hasPainPoints: !!pillarData?.pillarPainPoints,
      painPointsKeys: pillarData?.pillarPainPoints ? Object.keys(pillarData.pillarPainPoints) : [],
      hasCustomPainPoints: !!pillarData?.customPainPoints,
      customPainPointsKeys: pillarData?.customPainPoints ? Object.keys(pillarData.customPainPoints) : []
    })
    
    // Check if we have selected pillars data - try multiple sources
    const pillarsFromData = pillarData?.selectedPillars || []
    
    if (pillarsFromData.length > 0) {
      // Migrate any legacy format to canonical names
      const rawPillars = pillarsFromData
      const canonicalPillars = migrateToCanonicalPillars(rawPillars)
      
      console.log('[HYDRATE] Platform Configuration - canonical migration:', {
        rawPillars,
        canonicalPillars,
        migrated: JSON.stringify(rawPillars) !== JSON.stringify(canonicalPillars)
      })
      
      setSelectedPillars(canonicalPillars)
      
      if (pillarData.pillarPainPoints) {
        // Migrate pain point keys to canonical format and separate standard vs custom
        const migratedPainPoints: Record<string, string[]> = {}
        const migratedCustomPainPoints: Record<string, string[]> = {}
        
        Object.entries(pillarData.pillarPainPoints).forEach(([key, value]) => {
          const canonicalKey = migrateToCanonicalPillars([key])[0]
          const painPoints = value as string[]
          const standardOptions = (painPointOptions as any)[canonicalKey] || []
          
          // Separate standard and custom pain points with proper type handling
          const standardPoints = painPoints.filter((pp: string) => standardOptions.includes(pp))
          const customPoints = painPoints.filter((pp: string) => !standardOptions.includes(pp))
          
          migratedPainPoints[canonicalKey] = standardPoints
          if (customPoints.length > 0) {
            migratedCustomPainPoints[canonicalKey] = customPoints
          }
        })
        
        console.log('[HYDRATE] Platform Configuration - pain points separated:', {
          standardPainPoints: migratedPainPoints,
          customPainPoints: migratedCustomPainPoints
        })
        
        setPillarPainPoints(migratedPainPoints)
        setCustomPainPoints(migratedCustomPainPoints)
      }
      
      // Load custom pain points from separate field if available
      if (pillarData.customPainPoints) {
        console.log('[HYDRATE] Loading custom pain points from separate field:', pillarData.customPainPoints)
        setCustomPainPoints(pillarData.customPainPoints)
      }

      setHasLocalChanges(false)
    } else {
      console.log('[HYDRATE] Platform Configuration - no pillars found in pillar data, checking alternate sources')
      // Check if pillars are stored elsewhere in the blob
      if (data.valueSummary?.selectedPillars) {
        console.log('[HYDRATE] Found pillars in valueSummary:', data.valueSummary.selectedPillars)
        const canonicalPillars = migrateToCanonicalPillars(data.valueSummary.selectedPillars)
        setSelectedPillars(canonicalPillars)
      }
    }

    if (!hasHydratedRef.current) {
      setHasLocalChanges(false)
      hasHydratedRef.current = true
    }
  }, [data?.summary_data?.pillarSelection, data?.summary_data?.valueSummary])

  // Continue to next screen
  const handleContinue = async () => {
    console.log('🚀 [SAVE] Starting Platform Configuration final save operation');
    try {
      if (hasLocalChanges) {
        await persistPillarSelectionWithCurrent();
      }
      console.log('✅ [SAVE] Platform Configuration final save successful');
      
      // Navigate to value assessment summary with project ID
      const nextRoute = `/value-assessment/${projectId}/value-assessment-summary`
      
      setLocation(nextRoute);
    } catch (error) {
      console.error('❌ [SAVE] Failed to save Platform Configuration data:', error);
    }
  }

  // Go back to previous screen
  const handleBack = async () => {
    if (hasLocalChanges) {
      try {
        await persistPillarSelectionWithCurrent();
      } catch (error) {
        console.error('❌ [SAVE] Failed to save Platform Configuration data before navigating back:', error);
        return;
      }
    }

    setLocation(`/value-assessment/${projectId}/allocation`)
  }

  useUnsavedChangesWarning(hasLocalChanges, (options) => {
    persistPillarSelectionWithCurrent(options).catch(() => {})
  })

  const isPillarSelected = (pillarName: string) => selectedPillars.includes(pillarName)

  return (
    <div className="min-h-screen  text-white">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-blue-400 via-teal-400 to-green-400 bg-clip-text text-transparent">
            Platform Configuration
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Select the transformation areas most relevant to your organization's priorities
          </p>
        </div>

        {/* Pillar Selection with Integrated Pain Points */}
        <div className="space-y-6">
          {availablePillars.map((pillar) => {
            const isSelected = isPillarSelected(pillar.name)
            const pillarPainPointsSelected = pillarPainPoints[pillar.name] || []
            const pillarCustomPainPoints = customPainPoints[pillar.name] || []
            const availablePainPoints = painPointOptions[pillar.name as keyof typeof painPointOptions] || []
            
            return (
              <Card 
                key={pillar.name}
                className={`bg-gray-800/50 border transition-all duration-200 rounded-lg text-card-foreground shadow-sm bg-forestBlack border-[#334155] ${
                  isSelected 
                    ? 'border-primary bg-forestBlack/50' 
                    : 'border-gray-700'
                }`}
              >
                <CardHeader className="pb-4">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={(checked) => handlePillarToggle(pillar.name, checked as boolean)}
                      className="mt-1"
                      data-cy={`pillar-checkbox-${pillar.name.toLowerCase().replace(/\s+/g, '-')}`}
                    />
                    <div className="flex-1">
                      <div 
                        className="cursor-pointer" 
                        onClick={() => handlePillarToggle(pillar.name, !isSelected)}
                      >
                        <CardTitle className="text-white text-lg mb-2">{pillar.name}</CardTitle>
                        <p className="text-gray-400 text-sm mb-2">{pillar.description}</p>
                      </div>
                      
                      {/* Pain Points Section - Only show when pillar is selected */}
                      {isSelected && (
                        <div className="mt-4 space-y-4">
                          <div className="flex items-center gap-2">
                            <Target className="w-4 h-4 text-primary" />
                            <Label className="text-white font-medium">Key Pain Points</Label>
                          </div>
                          
                          {/* Standard Pain Points */}
                          {availablePainPoints.length > 0 && (
                            <div className="space-y-2">
                              <div className="flex flex-wrap gap-2">
                                {availablePainPoints.map((painPoint) => (
                                  <div key={painPoint} className="flex items-center space-x-2">
                                    <Checkbox
                                      id={`${pillar.name}-${painPoint}`}
                                      checked={pillarPainPointsSelected.includes(painPoint)}
                                      onCheckedChange={(checked) => 
                                        handlePainPointToggle(pillar.name, painPoint, checked as boolean)
                                      }
                                      data-cy={`pain-point-checkbox-${pillar.name.toLowerCase().replace(/\s+/g, '-')}-${painPoint.toLowerCase().replace(/\s+/g, '-')}`}
                                    />
                                    <Label 
                                      htmlFor={`${pillar.name}-${painPoint}`}
                                      className="text-sm text-gray-300 cursor-pointer"
                                    >
                                      {painPoint}
                                    </Label>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Custom Pain Points */}
                          {pillarCustomPainPoints.length > 0 && (
                            <div className="space-y-2">
                              <Label className="text-sm text-gray-400">Custom Pain Points:</Label>
                              <div className="space-y-1">
                                {pillarCustomPainPoints.map((customPoint) => (
                                  <div key={customPoint} className="flex items-center justify-between bg-gray-700/30 rounded px-3 py-2">
                                    <span className="text-sm text-gray-300">{customPoint}</span>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleRemoveCustomPainPoint(pillar.name, customPoint)}
                                      className="h-6 w-6 p-0 text-gray-400 hover:text-red-400"
                                    >
                                      <X className="w-3 h-3" />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Add Custom Pain Point */}
                          <div className="flex gap-2">
                            <Input
                              placeholder="Add custom pain point..."
                              value={newCustomPainPoint[pillar.name] || ''}
                              onChange={(e) => setNewCustomPainPoint(prev => ({ 
                                ...prev, 
                                [pillar.name]: e.target.value 
                              }))}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  handleAddCustomPainPoint(pillar.name)
                                }
                              }}
                              className="bg-gray-700/30 border-gray-600 text-white text-sm"
                            />
                            <Button
                              size="sm"
                              onClick={() => handleAddCustomPainPoint(pillar.name)}
                              disabled={!newCustomPainPoint[pillar.name]?.trim()}
                              className="bg-primary hover:bg-primaryHover text-white px-3"
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardHeader>
              </Card>
            )
          })}
        </div>

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <Button
            onClick={handleBack}
            variant="outline"
            className="border-[#334155] text-gray-300 hover:bg-forestBlack"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button
            onClick={handleContinue}
            className="bg-primary text-forestBlack hover:bg-primaryHover"
            disabled={selectedPillars.length === 0}
            data-cy="completePlatform"
          >
            Continue
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>


      </div>
    </div>
  )
}
